/* eslint-disable max-lines */
import { router } from '@redviking/argonaut-core-ui/src/routing/router';
import { actionTree, getStoreType, getterTree, mutationTree } from 'typed-vuex';
import { replacerListUtils } from '@redviking/argonaut-core-ui/src/util/composables/use-computed-list';
import { flowsTabRoute, screensTabRoute, scriptsTabRoute } from './util/consts';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';
import cellTypeMap from '@redviking/argonaut-core-ui/src/applet/design/cells/cellTypeMap';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { BaseNodeCfg, FlowCfg, NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { type AppletDesignVersionEntity } from './applet.entity';
import { generateDefaultGridLayout } from '@redviking/argonaut-core-ui/src/applet/design/screens/screen-preview/screenEditingUtil';
import {
    type GetAppletEventsResult,
    getAppletEvents,
} from '@redviking/argonaut-util/src/mes/get-applet-events';
import {
    type FindCellInCfgResp,
    findCellInCfg as findCellInCfgUtil,
    getAllAppletVariables,
    getDefaultAspectRatio,
} from '@redviking/argonaut-util/src/mes/appletUtil';

export type AllArgoCellUsesInAllScreenLayouts = {
    [key in keyof Latest.Screen.Designer.GridLayoutAspectRatios]: string[];
};

export type ScriptOrigin = {
    type: 'cell',
    screenId: string,
    cell: Latest.Screen.Cells.AppletCell,
} | {
    type: 'node',
    flowId: string;
    nodeId: string;
};

type HoveredData = {
    cellId: string | null;
    rowOrColumn: {
        idx: number;
        type: keyof Latest.Screen.Designer.LinkedGridCellCoordinate;
    } | null;
}

type DragData = {
    gridId: string;
    rowOrColNum: number;
    pos: { x: number, y: number };
    direction: 'left' | 'right' | 'up' | 'down';
} | null;

export type ScreenDesignerViewPortType = 'default' | 'wireframe';

export type SelectedFlowItemData = {
    title: string;
    subtitle?: string;
};

export type FlowCfgDeleteItemPayload = {
    id: string;
    kind: 'connection' | 'node';
} | {
    nodeId: string;
    inputKey: string;
    kind: 'nodeSocket';
    direction: 'input';
} | {
    nodeId: string;
    outputKey: string;
    kind: 'nodeSocket';
    direction: 'output';
};

export type FlowCfgUpdateSocketPortPayload = {
    nodeId: string;
    direction: 'input';
    oldInputKey: string;
    newInputKey: string;
} | {
    nodeId: string;
    direction: 'output';
    oldOutputKey: string;
    newOutputKey: string;
};

export type ActiveDialogState = {
    scriptId: string;
    kind: 'flowScriptEditor';
};

export type AppletDesignState = {
    dragData: DragData;
    currentSelectedCellIds: string[];
    activeScreenId: Latest.Screen.Config['id'] | null;
    /**
     * NOTE: this value persists even when not looking at the Vars tab, in order to allow
     * the user to easily go back to the Varp they're working on by clicking the Variables tab.
     */
    activeVarProviderId: string | null;
    activeScreenLayout: Latest.Screen.Designer.GridLayoutAspectRatio;
    // Allows any screen to be viewed in any supported aspect ratio
    overrideAspectRatio: Latest.Screen.Designer.GridLayoutAspectRatio | null;
    /** key: screenId, value: cellId */
    activeCellIdPerScreen: Record<string, string | null>;
    validatingScripts: boolean;
    /** key: tab base id, value: tab instance id
     * TODO: This needs to have another layer for screen id
    */
    activeTabPerTabGroup: Record<string, string>;
    screenDesignerViewType: ScreenDesignerViewPortType;
    hoveredData: HoveredData;
    activeScriptId: string | null;
    activeFlowId: string | null;
    screenDesignerInspectorDimensions: {
        width: number;
        height: number;
    };
    // keyed by flow id -> node id -> control name -> error message
    invalidNodeControls: Record<string, Record<string, Record<string, string>>>;
    // keyed by flow id -> node id -> error message
    invalidNodes: Record<string, Record<string, string>>;
    // keyed by flow id -> connection id -> error message
    invalidConnections: Record<string, Record<string, string>>;
    /**
     * This is a union of all dialogs/ modals/ prompts in an Applet. When this property is set it is expected that the corresponding popup is shown accordingly
     * NOTE: Right now I am only including one dialog as a POC, if this works out we should add other dialogs used in applets (i.e. save warning, media upload)
     * NOTE: Right now I am handling the store state in the component that implements the dialog, but in the future this could be moved to a more generic spot
    */
    activeDialogState: ActiveDialogState | null;
};

export const appletDesignState = (): AppletDesignState => ({
    dragData: null,
    activeScreenId: null,
    activeDialogState: null,
    activeVarProviderId: null,
    currentSelectedCellIds: [],
    overrideAspectRatio: null,
    activeScreenLayout: getDefaultAspectRatio(),
    screenDesignerViewType: 'default',
    activeCellIdPerScreen: {},
    activeTabPerTabGroup: {},
    validatingScripts: false,
    hoveredData: {
        cellId: null,
        rowOrColumn: null,
    },
    activeScriptId: null,
    activeFlowId: null,
    screenDesignerInspectorDimensions: {
        width: 0,
        height: 0,
    },
    invalidNodeControls: {},
    invalidNodes: {},
    invalidConnections: {},
});

export const mutations = mutationTree(appletDesignState, {
    /**
     * I don't know how to validate the monaco text model without setting the content to the scripts to validate on save.
     * This works fine, but if you're viewing any script tab, you'll see all the scripts fly by in the editor.
     * To workaround this, we show a "validating script" message under the editor so it's at least clear to the user what's happening... and looks intentional.
     */
    setValidatingScripts (state, payload: boolean) {
        state.validatingScripts = payload;
    },
    setActiveCell (state, payload: {
        screenId: string,
        /** if null, the screen will not have an active cell*/
        cellId: string | null,
    }) {
        if (payload.cellId) {
            // patch in
            state.activeCellIdPerScreen = {
                ...state.activeCellIdPerScreen,
                [payload.screenId]: payload.cellId,
            };
        } else {
            Vue.delete(state.activeCellIdPerScreen, payload.screenId);
        }
    },
    setScreenDesignerViewType (state, payload: ScreenDesignerViewPortType) {
        state.screenDesignerViewType = payload;
    },
    setDragData (state, payload: DragData) {
        state.dragData = payload;
    },
    setActiveScreenLayout (state, payload: Latest.Screen.Designer.GridLayoutAspectRatio) {
        state.activeScreenLayout = payload;
    },
    setOverrideAspectRatio (state, payload: Latest.Screen.Designer.GridLayoutAspectRatio | null) {
        state.overrideAspectRatio = payload;
    },
    setHoveredData (state, payload: HoveredData) {
        state.hoveredData = payload;
    },
    setSelection (state, payload: string[]) {
        state.currentSelectedCellIds = payload;
    },
    setActiveTabInstanceForTabViewingRefMap (state, payload: Record<string, string>) {
        state.activeTabPerTabGroup = payload;
    },
    setScreenDesignerInspectorDimensions (state, payload: { width: number, height: number }) {
        state.screenDesignerInspectorDimensions = payload;
    },
    setActiveDialogState (state, payload: ActiveDialogState | null) {
        state.activeDialogState = payload;
    },
    reset (state) {
        const newState: AppletDesignState = {
            dragData: null,
            activeDialogState: null,
            activeCellIdPerScreen: {},
            activeScreenId: null,
            activeVarProviderId: null,
            currentSelectedCellIds: [],
            overrideAspectRatio: null,
            activeScreenLayout: getDefaultAspectRatio(accessor.appletDesign.idMap.screens[accessor.appletDesign.activeScreenId || '']?.screen.gridLayoutAspectRatios),
            screenDesignerViewType: 'default',
            activeTabPerTabGroup: {},
            validatingScripts: false,
            hoveredData: {
                cellId: null,
                rowOrColumn: null,
            },
            activeScriptId: null,
            activeFlowId: null,
            screenDesignerInspectorDimensions: {
                width: 0,
                height: 0,
            },
            invalidNodeControls: {},
            invalidNodes: {},
            invalidConnections: {},
        };
        Object.assign(state, newState);
    },
    patchAppletDesignState (state, partialAppletDesignState: Partial<AppletDesignState>) {
        Object.assign(state, partialAppletDesignState);
    },
    setInvalidConnections (state, payload: { flowId: string; connectionId: string; message: string; }) {
        if (!state.invalidConnections[payload.flowId]) {
            Vue.set(state.invalidConnections, payload.flowId, {});
        }
        Vue.set(state.invalidConnections[payload.flowId], payload.connectionId, payload.message);
    },
    resetInvalidConnections (state) {
        state.invalidConnections = {};
    },
    setInvalidNodeControls (state, payload: {flowId: string, nodeId: string, controlName: string, message: string}) {
        if (!state.invalidNodeControls[payload.flowId]) {
            Vue.set(state.invalidNodeControls, payload.flowId, {});
        }
        if (!state.invalidNodeControls[payload.flowId][payload.nodeId]) {
            Vue.set(state.invalidNodeControls[payload.flowId], payload.nodeId, {});
        }
        Vue.set(state.invalidNodeControls[payload.flowId][payload.nodeId], payload.controlName, payload.message);
    },
    resetInvalidNodeControls (state) {
        state.invalidNodeControls = {};
    },
    setInvalidNodes (state, payload: {flowId: string, nodeId: string, message: string}) {
        if (!state.invalidNodes[payload.flowId]) {
            Vue.set(state.invalidNodes, payload.flowId, {});
        }
        Vue.set(state.invalidNodes[payload.flowId], payload.nodeId, payload.message);
    },
    resetInvalidNodes (state) {
        state.invalidNodes = {};
    },
});

const scriptGetters = {
    getScriptCfgById: () => {
        return (scriptId: string, useOriginalCfg?: boolean): Latest.RuntimeScriptCfg | null => {
            const scriptCfg = useOriginalCfg
                ? accessor.originalEntityAsType('appletDesignVersion')?.config.scripts.find(s => s.id === scriptId)
                : accessor.appletDesign.idMap.scripts[scriptId]?.script;
            if (!scriptCfg) {
                return null;
            }
            return scriptCfg;
        };
    },
    activeScript (state: AppletDesignState): Latest.RuntimeScriptCfg | null {
        if (state.activeScriptId) {
            return accessor.appletDesign.idMap.scripts[state.activeScriptId]?.script || null;
        }
        return null;
    },
};

const cellGetters = {
    /**
     * gets the active cell for the active screen (if exists)
     */
    activeCell (state: AppletDesignState): Latest.Screen.Cells.AppletCell | null {
        const activeScreenId = state.activeScreenId;
        if (activeScreenId && state.activeCellIdPerScreen[activeScreenId]) {
            return accessor.appletDesign.idMap.screens[activeScreenId]?.screen.cells.find(c => c.id === state.activeCellIdPerScreen[activeScreenId]) || null;
        }
        return null;
    },
    findCellInCfg: () => {
        function findCellInCfg<CELL extends Latest.Screen.Cells.AppletCell> (predicate: (cell: CELL) => boolean, useOriginalCfg?: boolean): FindCellInCfgResp<CELL> | null;
        function findCellInCfg (predicate: (cell: Latest.Screen.Cells.AppletCell) => boolean, useOriginalCfg?: boolean): FindCellInCfgResp<Latest.Screen.Cells.AppletCell> | null {
            const appletDesignVersionConfig = useOriginalCfg
                ? accessor.originalEntityAsType('appletDesignVersion')!.config
                : accessor.entityAsType('appletDesignVersion')!.config;
            return findCellInCfgUtil(appletDesignVersionConfig, predicate);
        }
        return findCellInCfg;
    },
    findCellsInCfg: () => {
        function findCellsInCfg<CELL extends Latest.Screen.Cells.AppletCell> (predicate: (cell: CELL) => boolean): {
            screenId: string,
            screenIdx: number,
            cellCfgs: CELL[],
        }[];
        function findCellsInCfg (predicate: (cell: Latest.Screen.Cells.AppletCell) => boolean): {
            screenId: string,
            screenIdx: number,
            cellCfgs: Latest.Screen.Cells.AppletCell[],
        }[] {
            const screens = accessor.entityAsType('appletDesignVersion')?.config?.screens || [];

            return screens.map((screen, screenIdx) => {
                return {
                    screenId: screen.id,
                    screenIdx,
                    cellCfgs: screen.cells.filter(predicate),
                };
            });
        }
        return findCellsInCfg;
    },
    getAllChildCells: () => {
        function getAllChildCells<CELL extends Latest.Screen.Cells.AppletCell> (cellId: string, useOriginalCfg?: boolean): {
            cellIdx: number;
            cellCfg: CELL;
        }[];
        function getAllChildCells (cellId: string, useOriginalCfg?: boolean): {
            cellIdx: number;
            cellCfg: Latest.Screen.Cells.AppletCell;
        }[] {
            const ret: {
                cellIdx: number;
                cellCfg: Latest.Screen.Cells.AppletCell;
            }[] = [];
            const screen = useOriginalCfg
                ? accessor.originalEntityAsType('appletDesignVersion')?.config?.screens.find(s => s.cells.find(c => c.id === cellId))
                : accessor.entityAsType('appletDesignVersion')?.config?.screens.find(s => s.cells.find(c => c.id === cellId));
            if (screen) {
                screen.cells.forEach((cell, cellIdx) => {
                    if (cell.attrs.parentCellId === cellId) {
                        ret.push({ cellIdx, cellCfg: cell });
                    }
                });
            }
            return ret;
        }
        return getAllChildCells;
    },
    getScreenForCell: () => {
        function getScreenForCell (cellId: string): Latest.Screen.Config | null;
        function getScreenForCell (cellId: string): Latest.Screen.Config | null {
            const screens = accessor.entityAsType('appletDesignVersion')?.config?.screens || [];
            return screens.find(screen => screen.cells.find(c => c.id === cellId)) || null;
        }
        return getScreenForCell;
    },
    getParentCell: () => {
        function getParentCell<CELL extends Latest.Screen.Cells.AppletCell> (cell: Latest.Screen.Cells.AppletCell): CELL | null;
        function getParentCell (cell: Latest.Screen.Cells.AppletCell): Latest.Screen.Cells.AppletCell | null {
            const screen = Object.values(accessor.appletDesign.idMap.screens).find(screenData => screenData && screenData.screen.cells.find(c => c.id === cell.id))?.screen;
            if (screen) {
                return screen.cells.find(c => c.id === cell.attrs.parentCellId) || null;
            }
            return null;
        }
        return getParentCell;
    },
};

export const getters = getterTree(appletDesignState, {
    /**
     * convenience util for getting the indexes and data for each of the important things in the config.
     *
     * Given only the ID of a thing, you can get the config object for it and where it is in the whole config.
     */
    idMap: (): {
        screens: Record<string, { idx: number, screen: Latest.Screen.Config } | undefined>;
        macros: Record<string, { idx: number, macro: Latest.Macros.AppletMacro } | undefined>;
        scripts: Record<string, { idx: number, script: Latest.RuntimeScriptCfg } | undefined>;
    } => {
        const entityData = accessor.entityAsType('appletDesignVersion');
        return {
            screens: (entityData?.config.screens || []).map((screen, idx) => ({ screen, idx })).reduce<Record<string, { idx: number, screen: Latest.Screen.Config }>>((prev, curr) => {
                prev[curr.screen.id] = curr;
                return prev;
            }, {}),
            macros: (entityData?.config.macros || []).map((macro, idx) => ({ macro, idx })).reduce<Record<string, { idx: number, macro: Latest.Macros.AppletMacro }>>((prev, curr) => {
                prev[curr.macro.id] = curr;
                return prev;
            }, {}),
            scripts: (entityData?.config.scripts || []).map((script, idx) => ({ script, idx })).reduce<Record<string, { idx: number, script: Latest.RuntimeScriptCfg }>>((prev, curr) => {
                prev[curr.script.id] = curr;
                return prev;
            }, {}),
        };
    },

    ...cellGetters,
    ...scriptGetters,

    allAppletVariables (): Record<string, Latest.AppletVariables.AppletVariable | Latest.AppletVariables.GeneratedAppletVariable> {
        const entityData = accessor.entityAsType('appletDesignVersion');
        if (!entityData) {
            return {};
        }
        return getAllAppletVariables(entityData.config);
    },
    allAppletFlowNodes (): Record<string, BaseNodeCfg> {
        const entityData = accessor.entityAsType('appletDesignVersion');
        if (!entityData) {
            return {};
        }
        let ret: Record<string, BaseNodeCfg> = {};
        for (const flow of Object.values(entityData.config.flows)) {
            ret = {
                ...ret,
                ...flow.nodes,
            };
        }

        return ret;
    },

    appletEvents (): GetAppletEventsResult {
        const entityData = accessor.entityAsType('appletDesignVersion');
        if (entityData) {
            return getAppletEvents(entityData.config);
        } else {
            return {};
        }
    },

    connectionsInActiveFlow (state): {
        [nodeId: string]: {
            inputs: {
                [socketKey: string]: FlowCfg['connections'];
            };
            outputs: {
                [socketKey: string]: FlowCfg['connections'];
            };
        };
    } {
        const result: {
            [nodeId: string]: {
                inputs: {
                    [socketKey: string]: FlowCfg['connections'];
                };
                outputs: {
                    [socketKey: string]: FlowCfg['connections'];
                };
            };
        } = {};
        const flowCfg = state.activeFlowId && accessor.entityAsType('appletDesignVersion')?.config?.flows[state.activeFlowId];
        if (!flowCfg) {
            return result;
        }
        for (const [ connId, connCfg ] of Object.entries(flowCfg.connections)) {
            const sourceNodeConnections = result[connCfg.sourceNodeId] || { inputs: {}, outputs: {} };
            result[connCfg.sourceNodeId] = sourceNodeConnections;
            const sourcePortConnections = sourceNodeConnections.outputs[connCfg.sourceNodePort] || {};
            sourceNodeConnections.outputs[connCfg.sourceNodePort] = sourcePortConnections;
            sourcePortConnections[connId] = connCfg;

            const targetNodeConnections = result[connCfg.targetNodeId] || { inputs: {}, outputs: {} };
            result[connCfg.targetNodeId] = targetNodeConnections;
            const targetPortConnections = targetNodeConnections.inputs[connCfg.targetNodePort] || {};
            targetNodeConnections.inputs[connCfg.targetNodePort] = targetPortConnections;
            targetPortConnections[connId] = connCfg;
        }
        return result;
    },
    getTabDetails: () => {
        // function returns null in order to prevent the flow ui from breaking from a thrown error
        const getTabDetails = (tabGroupIdInput: string): {
            tabGroupId: string;
            tabGroupName: string;
            tabs: {
                idx: number;
                name: string;
            }[];
        } | null => {
            const screens = accessor.entityAsType('appletDesignVersion')?.config?.screens || [];
            for (const screen of screens) {
                for (const gridLayout of Object.values(screen.gridLayoutAspectRatios)) {
                    const tabCells = gridLayout.linkedGridCells.filter(lgc => (lgc.type === 'tabGroup' && lgc.id === tabGroupIdInput) || (lgc.type === 'tabInstance' && lgc.parentGridId === tabGroupIdInput));
                    if (tabCells.length) {
                        let tabGroupId: string | null = null;
                        let tabGroupName: string | null = null;
                        const tabs: {
                            idx: number;
                            name: string;
                        }[] = [];
                        for (const tabCell of tabCells) {
                            // eslint-disable-next-line max-depth
                            if (tabCell.type === 'tabGroup') {
                                tabGroupId = tabCell.id;
                                tabGroupName = tabCell.name;
                            } else if (tabCell.type === 'tabInstance') {
                                tabs.push({
                                    idx: tabCell.idx,
                                    name: tabCell.additionalCellProperties.label,
                                });
                            }
                        }
                        if (!tabGroupId || !tabGroupName) {
                            return null;
                        }
                        return {
                            tabs,
                            tabGroupId,
                            tabGroupName,
                        };
                    }
                }
            }
            return null;
        };
        return getTabDetails;
    },
});

const cellActions = actionTree({
    state: appletDesignState,
    mutations,
    getters,
}, {
    addCell (ctx, payload: {
        screenId: string,
        cellCfg: Latest.Screen.Cells.AppletCell,
        /** @default true */
        activate?: boolean,
    }) {
        const screenCfg = accessor.appletDesign.idMap.screens[payload.screenId]?.screen;
        if (!screenCfg) {
            throw new Error('Screen not found');
        }

        accessor.appletDesign.setScreenCfg({
            ...screenCfg,
            cells: replacerListUtils.insertItem(screenCfg.cells, payload.cellCfg),
        });

        // should we activate this new cell?
        if (payload.activate ?? true) {
            accessor.appletDesign.setActiveCell({
                screenId: payload.screenId,
                cellId: payload.cellCfg.id,
            });
        }
    },
    deleteCell (ctx, payload: {
        screenId: string,
        cellCfg: Latest.Screen.Cells.AppletCell,
    }) {
        // cells might have custom delete process
        const typedDeleteCell = cellTypeMap[payload.cellCfg.type as keyof typeof cellTypeMap]?.delete;
        if (typedDeleteCell) {
            typedDeleteCell(payload.cellCfg);
        }

        // are there any cells that reference this cell as a parent?
        const childCells = accessor.appletDesign.getAllChildCells(payload.cellCfg.id);
        if (childCells && childCells.length > 0) {
            childCells.forEach(cellData => {
                accessor.appletDesign.deleteCell({
                    cellCfg: cellData.cellCfg,
                    screenId: payload.screenId,
                });
            });
        }

        // ensure cell is deactivated
        const activeCellForScreen = accessor.appletDesign.activeCellIdPerScreen[payload.screenId];
        if (activeCellForScreen === payload.cellCfg.id) {
            accessor.appletDesign.setActiveCell({ screenId: payload.screenId, cellId: null });
        }

        // remove the cell from any grid layout cells
        accessor.appletDesign.removeCellFromScreen({
            screenId: payload.screenId,
            cellId: payload.cellCfg.id,
        });
    },
    removeCellFromScreen (ctx, payload: {
        cellId: string;
        screenId: string;
    }) {
        const screenCfg = accessor.appletDesign.idMap.screens[payload.screenId]!.screen;
        const gridLayoutAspectRatio = screenCfg.gridLayoutAspectRatios;


        Object.entries(gridLayoutAspectRatio).forEach(([ aspectRatio, gridLayout ]) => {
            gridLayoutAspectRatio[aspectRatio as keyof typeof gridLayoutAspectRatio] = {
                ...gridLayout,
                linkedGridCells: gridLayout.linkedGridCells.map(lgc => {
                    if (lgc.type === 'regular' && lgc.additionalCellProperties.argoCellId === payload.cellId) {
                        const newLgc: Latest.Screen.Designer.RegularGridCell = {
                            ...lgc,
                            additionalCellProperties: {
                                argoCellId: '',
                            },
                        };
                        return newLgc;
                    }
                    return lgc;
                }),
            };
        });
        screenCfg.gridLayoutAspectRatios = gridLayoutAspectRatio;
        const cellIdx = screenCfg.cells.findIndex(c => c.id === payload.cellId);
        if (cellIdx !== -1) {
            accessor.appletDesign.setScreenCfg({
                ...screenCfg,
                cells: replacerListUtils.deleteItem(screenCfg.cells, cellIdx),
            });
        }
    },
    updateCell (ctx, payload: { screenId: string, cellCfg: Latest.Screen.Cells.AppletCell }) {
        const screenCfg = accessor.appletDesign.idMap.screens[payload.screenId]!.screen;
        accessor.appletDesign.setScreenCfg({
            ...screenCfg,
            cells: replacerListUtils.updateItem(screenCfg.cells, payload.cellCfg, screenCfg.cells.findIndex(c => c.id === payload.cellCfg.id)),
        });
    },
});

const screenActions = actionTree({
    state: appletDesignState,
    mutations,
    getters,
}, {
    setScreens (ctx, screens: Latest.Screen.Config[]) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const newEntityData: typeof entityData = { ...entityData };
        newEntityData.config.screens = screens;
        accessor.setPageEntity({ type: 'appletDesignVersion', entity: newEntityData });
    },
    setScreenCfg (ctx, screenCfg: Latest.Screen.Config) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const screenIdx = accessor.appletDesign.idMap.screens[screenCfg.id]!.idx;
        const newScreens = replacerListUtils.updateItem(entityData.config.screens, screenCfg, screenIdx);
        accessor.appletDesign.setScreens(newScreens);
    },
    setActiveScreen ({ commit }, payload: { screenId: string | null }) {
        const patchPayload: Partial<AppletDesignState> = {
            activeScreenId: payload.screenId,
        };
        commit('patchAppletDesignState', patchPayload);

        // if we're not at the screen route, go there
        if (payload.screenId && router.currentRoute.name !== screensTabRoute) {
            router.replace({ name: screensTabRoute, query: router.currentRoute.query });
        }
        const activeScreenLayout = getDefaultAspectRatio(accessor.appletDesign.idMap.screens[payload.screenId || '']?.screen.gridLayoutAspectRatios);
        accessor.appletDesign.setActiveScreenLayout(activeScreenLayout);

        const screens = accessor.entityAsType('appletDesignVersion')!.config.screens;
        const gridLayout = (screens.find(s => s.id === payload.screenId) || screens[0])!.gridLayoutAspectRatios[activeScreenLayout];

        /**
         * when the active screen gets set, perform the following:
         *
         * 1. check the store for currently viewed tabs. If a tab group is not represented, but exists, set the reference to the first tab
         * 2. set the targeted grid id to the grid config id to ensure that it is always set to something
         */
        const activeTabInstanceForTabViewingRefMap: Record<string, string> = {};

        for (const tabGroup of gridLayout.linkedGridCells.filter((lgc): lgc is Latest.Screen.Designer.TabGroupCell => lgc.type === 'tabGroup')) {
            const tabInstanceCell = gridLayout.linkedGridCells.find(lgc => lgc.type === 'tabInstance' && lgc.parentGridId === tabGroup.id && lgc.idx === 0);
            if (!tabInstanceCell) {
                throw new Error('First tab not found for tab group');
            }
            activeTabInstanceForTabViewingRefMap[tabGroup.id] = tabInstanceCell.id;
        }
        accessor.appletDesign.setScreenDesignerViewType('default');
        accessor.appletDesign.setActiveTabInstanceForTabViewingRefMap(activeTabInstanceForTabViewingRefMap);
        accessor.appletDesign.setSelection([]);
        accessor.appletDesign.setHoveredData({
            cellId: null,
            rowOrColumn: null,
        });
    },
    addNewScreen () {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const newScreen: Latest.Screen.Config = {
            cells: [],
            id: uuidv4(),
            name: 'New Screen',
            gridLayoutAspectRatios: {
                mobile: generateDefaultGridLayout(false),
                tablet: generateDefaultGridLayout(false),
                desktop: generateDefaultGridLayout(true),
                largeDisplay: generateDefaultGridLayout(false),
            },
            activeOperator: { required: Boolean(entityData.config.activeOperator) },
        };

        accessor.appletDesign.setScreens([
            ...entityData.config.screens,
            newScreen,
        ]);
        accessor.appletDesign.setActiveScreen({ screenId: newScreen.id });
    },
    removeScreen (ctx, screen: Latest.Screen.Config) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        accessor.appletDesign.setActiveCell({ screenId: screen.id, cellId: null });

        if (accessor.appletDesign.activeScreenId === screen.id) {
            accessor.appletDesign.setActiveScreen({ screenId: null });
        }

        screen.cells.forEach(cell => {
            accessor.appletDesign.deleteCell({
                cellCfg: cell,
                screenId: screen.id,
            });
        });

        const tabGroupRootIds: string[] = [];
        Object.values(screen.gridLayoutAspectRatios).forEach(gridLayout => {
            gridLayout.linkedGridCells.forEach(lgc => {
                if (lgc.type === 'tabGroup') {
                    tabGroupRootIds.push(lgc.id);
                }
            });
        });
        const copyTabGroupViewingRefMap = { ...accessor.appletDesign.activeTabPerTabGroup };
        tabGroupRootIds.forEach(tabGroupRootId => {
            delete copyTabGroupViewingRefMap[tabGroupRootId];
        });
        accessor.appletDesign.setActiveTabInstanceForTabViewingRefMap(copyTabGroupViewingRefMap);

        accessor.appletDesign.setScreens(entityData.config.screens.filter(s => s.id !== screen.id));

        accessor.appletDesign.setScreenDesignerViewType('default');
        accessor.appletDesign.setActiveTabInstanceForTabViewingRefMap({});
        accessor.appletDesign.setSelection([]);
        accessor.appletDesign.setHoveredData({
            cellId: null,
            rowOrColumn: null,
        });
    },
});

const macroActions = actionTree({
    state: appletDesignState,
    mutations,
    getters,
}, {
    addMacro (ctx, payload: {
        screenId: string;
        macroCfg: Latest.Macros.AppletMacro;
    }) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;

        const newEntity: AppletDesignVersionEntity = {
            ...entityData,
            config: {
                ...entityData.config,
                macros: [
                    ...entityData.config.macros || [],
                    payload.macroCfg,
                ],
            },
        };

        // insert macro
        accessor.setPageEntity({
            entity: newEntity,
            type: 'appletDesignVersion',
        });
    },
    /**
     * updates a macro config, and optionally the inputs and outputs for its varp target
     */
    setMacroCfg (ctx, payload: { macroCfg: Latest.Macros.AppletMacro, inputs?: string[], outputs?: string[] }) {
        let entityData = accessor.entityAsType('appletDesignVersion')!;
        const { idx: macroIdx } = accessor.appletDesign.idMap.macros[payload.macroCfg.id]!;

        // ensure the value here is up to date
        entityData = accessor.entityAsType('appletDesignVersion')!;

        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: {
                ...entityData,
                config: {
                    ...entityData.config,
                    macros: replacerListUtils.updateItem(entityData.config.macros!, payload.macroCfg, macroIdx),
                },
            },
        });
    },
    removeMacroCfg (ctx, payload: {
        macroId: string;
    }) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const newEntityData: AppletDesignVersionEntity = {
            ...entityData,
            config: {
                ...entityData.config,
                macros: entityData.config.macros.filter(m => m.id !== payload.macroId),
            },
        };
        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: newEntityData,
        });
    },
});

const scriptActions = actionTree({
    getters,
    mutations,
    state: appletDesignState,
}, {
    setActiveScriptId (ctx, scriptId: string | null) {
        ctx.commit('patchAppletDesignState', { activeScriptId: scriptId });
        if (scriptId && router.currentRoute.name !== scriptsTabRoute) {
            router.replace({ name: scriptsTabRoute, query: router.currentRoute.query });
        }
    },
    addScript (ctx, scriptCfg: Latest.RuntimeScriptCfg) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const newEntityData: AppletDesignVersionEntity = {
            ...entityData,
            config: {
                ...entityData.config,
                scripts: [
                    ...entityData.config.scripts,
                    scriptCfg,
                ],
            },
        };
        accessor.setPageEntity({ entity: newEntityData, type: 'appletDesignVersion' });
    },
    removeScript (ctx, payload: {
        scriptId: string;
    }) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const scriptCfg = accessor.appletDesign.idMap.scripts[payload.scriptId]?.script;
        if (!scriptCfg) {
            throw new Error('Could not find script value to remove');
        }
        const newEntityData: AppletDesignVersionEntity = {
            ...entityData,
            config: {
                ...entityData.config,
                scripts: entityData.config.scripts.filter(s => s.id !== payload.scriptId),
            },
        };
        accessor.setPageEntity({ entity: newEntityData, type: 'appletDesignVersion' });
    },
    setScript (ctx, scriptCfg: Latest.RuntimeScriptCfg) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const scriptIdx = accessor.appletDesign.idMap.scripts[scriptCfg.id]?.idx;
        if (scriptIdx !== undefined && scriptIdx !== -1) {
            const newEntityData: AppletDesignVersionEntity = {
                ...entityData,
                config: {
                    ...entityData.config,
                    scripts: [
                        ...entityData.config.scripts.slice(0, scriptIdx),
                        scriptCfg,
                        ...entityData.config.scripts.slice(scriptIdx + 1),
                    ],
                },
            };
            accessor.setPageEntity({ entity: newEntityData, type: 'appletDesignVersion' });
        }
    },
});

const actions = actionTree({
    state: appletDesignState,
    mutations,
    getters,
}, {
    ...cellActions,
    ...screenActions,
    ...macroActions,
    ...scriptActions,
    setActiveFlowId ({ commit }, flowId: string | null) {
        commit('patchAppletDesignState', { activeFlowId: flowId });
        if (flowId && (router.currentRoute.name !== flowsTabRoute || router.currentRoute.query.flowId !== flowId)) {
            router.replace({
                name: flowsTabRoute,
                query: {
                    ...router.currentRoute.query,
                    flowId,
                },
            });
        } else if (!flowId && router.currentRoute.query.flowId) {
            const query = { ...router.currentRoute.query };
            delete query.flowId;
            router.replace({
                name: router.currentRoute.name || flowsTabRoute,
                query,
            });
        }
    },
    addFlow (ctx, payload: {
        name: string;
        kind: 'regular' | 'functional' | 'privileged';
    }) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const id = getUID();
        const newEntityData = { ...entityData };
        if (payload.kind === 'regular') {
            const startNodeCfg: NodeCfg<'start'> = {
                id: 'start',
                kind: 'start',
                disabled: false,
            };
            newEntityData.config.flows[id] = {
                id,
                groups: {},
                comments: {},
                connections: {},
                kind: 'regular',
                name: payload.name,
                nodes: {
                    start: startNodeCfg,
                },
            };
        } else if (payload.kind === 'functional') {
            const functionalInputsNodeId = getUID();
            const functionalInputsNode: NodeCfg<'functionalInputs'> = {
                id: functionalInputsNodeId,
                kind: 'functionalInputs',
                controlValues: {
                    inputs: [],
                },
            };
            const functionalOutputsNodeId = getUID();
            const functionalOutputsNode: NodeCfg<'functionalOutputs'> = {
                x: 300,
                id: functionalOutputsNodeId,
                kind: 'functionalOutputs',
                controlValues: {
                    outputs: [],
                },
            };
            newEntityData.config.flows[id] = {
                id,
                groups: {},
                comments: {},
                connections: {},
                kind: 'functional',
                name: payload.name,
                nodes: {
                    [functionalInputsNodeId]: functionalInputsNode,
                    [functionalOutputsNodeId]: functionalOutputsNode,
                },
                
            };
        } else if (payload.kind === 'privileged') {
            const privilegedInputsNodeId = getUID();
            const privilegedInputsNode: NodeCfg<'functionalInputs'> = {
                id: privilegedInputsNodeId,
                kind: 'functionalInputs',
                controlValues: {
                    inputs: [],
                },
            };
            const privilegedOutputsNodeId = getUID();
            const privilegedOutputsNode: NodeCfg<'functionalOutputs'> = {
                x: 300,
                id: privilegedOutputsNodeId,
                kind: 'functionalOutputs',
                controlValues: {
                    outputs: [],
                },
            };
            newEntityData.config.flows[id] = {
                id,
                groups: {},
                comments: {},
                connections: {},
                kind: 'privileged',
                name: payload.name,
                nodes: {
                    [privilegedInputsNodeId]: privilegedInputsNode,
                    [privilegedOutputsNodeId]: privilegedOutputsNode,
                },
            };
        }
        accessor.setPageEntity({ type: 'appletDesignVersion', entity: newEntityData });
        accessor.appletDesign.setActiveFlowId(id);
    },
    deleteFlow (_, payload: { flowId: string }) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const newEntityData = { ...entityData };
        delete newEntityData.config.flows[payload.flowId];
        if (accessor.appletDesign.activeFlowId === payload.flowId) {
            accessor.appletDesign.setActiveFlowId('');
        }
        accessor.setPageEntity({ type: 'appletDesignVersion', entity: newEntityData });
    },
    updateFlowCfg (_, payload: FlowCfg) {
        const entityData = accessor.entityAsType('appletDesignVersion')!;
        const newEntityData = { ...entityData };
        newEntityData.config.flows[payload.id] = payload;
        accessor.setPageEntity({ type: 'appletDesignVersion', entity: newEntityData });
    },
});

export const appConfigStore = {
    namespaced: true,
    state: appletDesignState,
    getters,
    mutations,
    actions,
};

const storeType = getStoreType(appConfigStore);
export type AppConfigStore = typeof storeType;

export default appConfigStore;
